import { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Plus, Minus, Video, Upload, Link as LinkIcon } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

type Batch = {
  id: string;
  name: string;
}

type Recording = {
  id?: string;
  video: string;
  batchId: string;
  videoFile?: File;
}

interface RecordingsListProps {
  recordings: Recording[];
  availableBatches: Batch[];
  onRecordingsChange: (recordings: Recording[]) => void;
}

const RecordingsList = ({ recordings, availableBatches, onRecordingsChange }: any) => {
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  const addRecording = () => {
    const newRecording: Recording = {
      id: Date.now().toString(),
      video: '',
      batchId: availableBatches?.length > 0 ? availableBatches[0].id : ''
    };

    onRecordingsChange([...recordings, newRecording]);
  };

  const removeRecording = (id: string, batchId: string) => {
    onRecordingsChange(recordings?.filter(recording => recording.id !== id && recording.batchId !== batchId));
  };

  const updateRecording = (batchId: string, id: string, updates: Partial<Recording>) => {
    onRecordingsChange(
      recordings?.map(recording =>
        recording.batchId === batchId && recording.id == id ? { ...recording, ...updates } : recording
      )
    );
  };

  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, recordingId: string) => {
    const file = e.target.files?.[0];
    if (file) {
      // In a real app, we would upload the file to a server here
      // For this demo, we'll just simulate storing the file
      updateRecording(recordingId, Date.now().toString(), {
        video: '',
        videoFile: file
      });
    }
  };

  return (
    <div className="space-y-4 border rounded-lg p-4 max-h-[80vh] overflow-y-auto">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-bytesplash-blue">Recordings</h3>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addRecording}
        >
          <Plus className="mr-1 h-4 w-4" />
          Add Recording
        </Button>
      </div>

      {recordings?.map((recording, index) => (
        <div key={recording.id} className="grid grid-cols-1 gap-4 items-start border-b pb-4 last:border-0">
          <div className="flex items-center gap-2">
            <span className="text-bytesplash-blue font-medium">
              Recording {index + 1}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
              onClick={() => removeRecording(recording.id, recording.batchId)}
            >
              <Minus className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Video Source</Label>
              <Tabs defaultValue="link" className="w-full">
                <TabsList className="grid grid-cols-2 mb-2">
                  {/* <TabsTrigger value="upload">Upload Video</TabsTrigger> */}
                  <TabsTrigger value="link">Video Link</TabsTrigger>
                </TabsList>

                <TabsContent value="upload" className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      value={recording.videoFile ? recording.videoFile.name : ''}
                      readOnly
                      placeholder="Select a video to upload"
                      className="flex-1"
                    />
                    <input
                      type="file"
                      id={`video-file-${recording.id}`}
                      accept="video/*"
                      className="hidden"
                      onChange={(e) => handleFileChange(e, recording.id)}
                      ref={el => fileInputRefs.current[recording.id] = el}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (fileInputRefs.current[recording.id]) {
                          fileInputRefs.current[recording.id]?.click();
                        }
                      }}
                    >
                      <Upload className="h-4 w-4" />
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="link" className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Video className="h-4 w-4 text-gray-500 flex-shrink-0" />
                    <Input
                      id={`video-link-${recording.id}`}
                      value={recording.video}
                      onChange={(e) => updateRecording(recording.batchId, recording.id, {
                        video: e.target.value
                      })}
                      placeholder="e.g., https://youtu.be/example"
                      className={!isValidUrl(recording?.video) && recording.video ? "border-red-300" : ""}
                    />
                  </div>
                  {!isValidUrl(recording.video) && recording.video && (
                    <p className="text-xs text-red-500">Please enter a valid URL</p>
                  )}
                </TabsContent>
              </Tabs>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`batch-${recording.batchId}`}>
                Select Batch<span className="text-red-500">*</span>
              </Label>
              <Select
                value={recording.batchId}
                onValueChange={(value) =>
                  updateRecording(recording.batchId, recording.id, { batchId: value })
                }
              >
                <SelectTrigger id={`batch-${recording.id}`}>
                  <SelectValue placeholder="Select a batch" />
                </SelectTrigger>
                <SelectContent>
                  {availableBatches?.map(batch => (
                    <SelectItem key={batch.id} value={batch.id}>
                      {batch.batchName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {
            index == recordings?.length - 1 &&
            <div className="pt-4">
              <hr />
              <div className="pt-4">

                <Button
                  type="button"
                  size="sm"
                  onClick={addRecording}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Add Recording
                </Button>
              </div>

            </div>
          }

        </div>
      ))}
      {recordings?.length === 0 && (
        <div className="text-center py-8 border rounded-md bg-gray-50">
          <p className="text-gray-500 mb-2">No recordings added yet.</p>
          <Button
            type="button"
            variant="outline"
            onClick={addRecording}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add First Recording
          </Button>
        </div>
      )}
    </div>
  );
};

export default RecordingsList;
